import {Component, Inject, Input, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {TranslocoService} from '@jsverse/transloco';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {Store} from '@ngrx/store';
import {BehaviorSubject, combineLatest, filter, firstValueFrom, Subject, switchMap} from 'rxjs';
import {delay, map, startWith, take, takeUntil, tap} from 'rxjs/operators';
import {GlobalService} from '../../../../../global/global.service';
import {FilesValidatorsService} from '../../../../shared/validators/files-validators';

import * as ContractControlAction from './../../store/actions/contract-control.action';
import {IGenerateActivityFormData} from './ai-generate-activity/ai-generate-activity.component';
import {BulkUploadStepOrderService} from './bulk-upload-step-order.service';
import {IStepOrderService, IStepOrderServiceToken} from './step-order.service';
import {BulkUploadStepsEnum} from './steps/bulkUploadStepsEnum';
import {IImportedSummaryData, IUnimportedSummaryData} from './steps/import-summary-step/import-summary-step.component';
import {IMatchContractCardsTableItem} from './steps/match-contract-cards-step/match-contract-cards-step.component';
import {ICCBulkUploadFilesService, IImportResponseDTO, ISetImportSettingsRequest, IUploadFilesServiceToken, UploadFilesService} from './upload-files.service';
import {UploadPDFUpload} from './upload-pdf-common';
import { DownloadFileService } from 'app/global/download-file.service';
import { ICategory } from 'app/main/shared/shared/table/components/category-name/category-name.component';
import { ContractCardItemsService } from './contract-card-items.service';

@Component({
    selector: 'app-bulk-upload-modal',
    templateUrl: './bulk-upload-modal.component.html',
    styleUrls: ['./bulk-upload-modal.component.scss'], 
    providers: [
        {
            provide: IUploadFilesServiceToken, useExisting: UploadFilesService
        },
    ]
})
export class BulkUploadModalComponent extends UploadPDFUpload implements OnInit, OnDestroy {

    @Input() maxFileSize:number
    private untilDestroy$: Subject<void> = new Subject<void>();
    acceptableCSVFiles: string[] = ["application/vnd.ms-excel", "text/csv", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"];

    importedSummary: IImportedSummaryData[] = [];
    unimportedSummary: IUnimportedSummaryData[] = [];


    // Lading masks
    isCSVUploadInProgress = false;
    isSkipStepLoading = false;

    csvFileSubmitted:boolean = false;

    private isActivityFormDataValid: boolean;
    shouldAddActivityToContractCard: boolean = false;
    uploadCSVForm: FormGroup;
    uploadCSVFormFilesValidation: ValidatorFn[];
    importCSVResponse$ = this.stepOrderService.importCSVResponse$.pipe(map(x=> x?.pdf_matching ?? []))

    contractCards$: BehaviorSubject<ICategory[]> = new BehaviorSubject<ICategory[]>([]);

    /**
     * Contructor
     */
    constructor(@Inject(IStepOrderServiceToken) public stepOrderService: IStepOrderService, 
        private activeModal: NgbActiveModal, 
        private store: Store, 
        translocoService: TranslocoService, 
        globalService: GlobalService, 
        formBuilder: FormBuilder, 
        @Inject(IUploadFilesServiceToken) uploadService: ICCBulkUploadFilesService, 
        private filesValidatorsService:FilesValidatorsService, 
        @Inject(IUploadFilesServiceToken) private uploadCCService: ICCBulkUploadFilesService, 
        private downloadFileService: DownloadFileService,
        private contractCardItemsService: ContractCardItemsService)  {
        super(formBuilder, uploadService, globalService, translocoService);
    }

    ngOnInit(): void {
        this.stepOrderService.init();
        this.uploadCSVFormFilesValidation= [
            this.filesValidatorsService.fileTypeValidator(this.acceptableCSVFiles),
            Validators.required
        ]
        
        this.uploadCSVForm = new FormGroup({
            document_type_id:new FormControl(null,  [Validators.required]),
            file: new FormControl(null, this.uploadCSVFormFilesValidation),
        });

        combineLatest({
            ccs:this.contractCardItemsService.contractCard$,
            ccsOptions: this.stepOrderService.importCSVResponse$.pipe(
                startWith(null),    
                map(x=> x?.options?.contract_controls ?? [])
            )
        })
        
        .pipe(
          takeUntil(this.untilDestroy$)
        )
        .subscribe(({ccs, ccsOptions}) =>{
          this.contractCards$.next(
              [
                  ...ccs,
                  ...ccsOptions.filter(x=> ccs.some(y=>y.id === x.id) === false)
              ]
          )
        });

        this.contractCardItemsService.refreshContractCard();
 
    }

    ngOnDestroy(): void {
        this.untilDestroy$.next();
        this.untilDestroy$.complete();
        this.stepOrderService.reset();
    }

    public uploadedFilesWithContractCards: IMatchContractCardsTableItem[] = [];

    /**
     * close the Bulk Upload modal
     */
    bulkUploadModalClose(action: string) {
        this.activeModal.close(action);
    }

    /**
     * Function for next step
     */
    next() {
        this.stepOrderService.next();
    }

    async uploadPdfFiles() {
         super.uploadPdfFile({importId:this.stepOrderService.importId})
            .pipe(
                filter(x => x.every(y=>y.progress === 100) ),
                take(1),
                delay(1000) //Make a delay to show the progress bar for all files
            )
            .subscribe({next:()=>{
                    this.next();
            }})
        
    }

    /**
     * Function for next step
     */
    skip() {
        this.stepOrderService.currentStep$
            .pipe(take(1))
            .subscribe(current => {
                if (current == 0) {
                    this.isSkipStepLoading = true;
                    this.stepOrderService.CSVImportSkipped = true;

                    (<ICCBulkUploadFilesService>this.uploadService).createImport()
                        .pipe(
                            tap((importData) => {
                            this.stepOrderService.importId = importData.id;
                            this.stepOrderService.importSettings = importData.settings?.todo ?? null;
                            this.stepOrderService.mergeOrganisations = importData?.settings?.merge_organizations ?? false;
                            this.stepOrderService.first_line_titles = importData?.settings?.first_line_titles === true ? 1 : 0 ;
                        }),
                        takeUntil(this.untilDestroy$))
                        .subscribe({
                            next: () => {
                                this.stepOrderService.moveToStep(BulkUploadStepsEnum.UPLOAD_PDF);
                            }, error: () => {
                                this.globalService.toastrError(this.translocoService.translate('Obtain import id request fails toast (bulk upload) - Could not establish import id. Please upload import structure or try again later'));
                            }, complete: () => {
                                this.isSkipStepLoading = false;
                            }
                        });
                }
            });
    }

    /**
     * Function for prev step
     */
    prev() {
        this.stepOrderService.previous();
    }

    onCSVUploaded(id: number) {
        this.stepOrderService.importId = id;
        this.stepOrderService.onMappingChange([]);
        this.stepOrderService.fetchFirstRow(id);
        this.stepOrderService.fetchPotentialMappings();
    }

    onUploadSettingsChanged(settings: ISetImportSettingsRequest) {
        
        this.stepOrderService.first_line_titles = settings?.first_line_titles === true ? 1 as const : 0 as const;
        this.stepOrderService.mergeOrganisations = settings?.merge_organizations;
        this.stepOrderService.importSettings = settings?.todo;

    }

    onUploadCSVLoadingStateChange(isLoading: boolean) {
        this.isCSVUploadInProgress = isLoading;
    }

    onCSVFileLoaded() {
        this.next();
    }

    onMappingChange(mappings) {
        this.stepOrderService.onMappingChange(mappings);
    }

    onPreviewNext() {
        this.next();
    }

    importXLS() {
        this.stepOrderService.setImportInProgress(true);
        (<ICCBulkUploadFilesService>this.uploadService).setImportSettings(this.stepOrderService.importId, {
            first_line_titles: this.stepOrderService.first_line_titles === 1,
            merge_organizations: this.stepOrderService.mergeOrganisations,
            todo: null
        }).pipe(takeUntil(this.untilDestroy$), switchMap(x => (<ICCBulkUploadFilesService>this.uploadService).importXls(x.id, x.settings.first_line_titles === true ? 1 as const : 0 as const, x.settings.merge_organizations))).subscribe({
            next: ( response) => {
                this.stepOrderService.setImportCompleted(true);
                this.handleResponseActions(response);
            }, error: () => {
                this.globalService.toastrError(this.translocoService.translate('Data structure request fail toast  (bulk upload) - Import data structure failed'));
                this.stepOrderService.setImportInProgress(false);
            }, complete: () => {
                this.stepOrderService.setImportInProgress(false);
            }
        });
    }
 
    onImportPDFClick() {
        (<ICCBulkUploadFilesService>this.uploadService).importPDF(this.stepOrderService.importId)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this.bulkUploadModalClose('close');
                    this.store.dispatch(ContractControlAction.importPDFSuccessNotification());
                 
                }, error: (error) => {
                    this.globalService.toastrError(this.translocoService.translate('Import failed toast (bulk upload) - Import PDF failed') + `\n${error.message}`);
                }
            });
    }


    firstLineTitlesChange(firstLineTitles: 0 | 1) {
        this.stepOrderService.first_line_titles = firstLineTitles;
        this.stepOrderService.fetchFirstRow(this.stepOrderService.importId);
    }

    protected readonly BulkUploadStepsEnum = BulkUploadStepsEnum;
    uploadModalSubmitted: boolean = false;

    onActivityFormDataChanged(activityFormData: IGenerateActivityFormData) {
        this.stepOrderService.importSettings = activityFormData;
    }

    onActivityFormDataValidityChanged($event: boolean) {
        this.isActivityFormDataValid = $event;
    }

    private async saveActivitySettings(){
        if( this.shouldAddActivityToContractCard && this.isActivityFormDataValid  &&  this.stepOrderService.importSettings){
            return firstValueFrom((<ICCBulkUploadFilesService>this.uploadService).setImportSettings(this.stepOrderService.importId, {
                 first_line_titles: this.stepOrderService.first_line_titles === 1,
                 merge_organizations: this.stepOrderService.mergeOrganisations ?? false,
                 todo: {
                     user_id: this.stepOrderService.importSettings.user_id,
                     description: this.stepOrderService.importSettings.description,
                     due_date: this.stepOrderService.importSettings.due_date
                 }
             }));
        }else{
            return;
        }

    }

    async onMoveToNextStepFromUploadPDF() {
        this.uploadModalSubmitted = true;
        if (this.isActivityFormDataValid === true || this.isActivityFormDataValid == null) {
           try{
               await this.saveActivitySettings();
               super.uploadPdfFile({importId:this.stepOrderService.importId})
                   .pipe(
                       filter(x => x.every(y=>y.progress === 100) || x.length === 0 ),   
                       take(1),
                       delay(1000) //Make a delay to show the progress bar for all files
                   )
                   .subscribe({
                       next: () => {
                           this.next();
                       }
                   });
           }catch (e){
               console.log('error', e);
           }
        }
    }

    onShouldAddActivityToContractCardChange($event: boolean) {
        if($event === false){
            this.stepOrderService.importSettings = null;
        }
    }
    
    private handleResponseActions( importResponse: IImportResponseDTO) {
        this.stepOrderService.setImportCSVResponse(importResponse) ;
        this.importIXLSSucceeded();
        
    }
    
    private async importIXLSSucceeded(){
        this.stepOrderService.next();
        this.globalService.toastrSuccess(this.translocoService.translate('Data structure request success toast  (bulk upload) - Import data structure has been imported'));
    }

    onExportFailedDataClicked() {
        this.uploadCCService.exportFailedData(this.stepOrderService.importId)
        .pipe(take(1))
        .subscribe({
            next: (response) => {
                this.downloadFileService.downloadCSVFile(response, 'failed_import');
            }, 
            error: (error) => {
                this.globalService.toastrError(error);
            }
        });
    }

    uploadCSV() {
      this.csvFileSubmitted = true;  
      if(this.uploadCSVForm.valid){

          this.onUploadCSVLoadingStateChange(true);
          this.stepOrderService.documentTypeId = +this.uploadCSVForm.get('document_type_id')?.value;
          this.uploadCCService.uploadXLSFile(this.uploadCSVForm.get('file')?.value, this.stepOrderService.documentTypeId)
              .pipe(takeUntil(this.untilDestroy$))
              .subscribe({
                      next: response => {
                          this.onCSVUploaded(response.id);
                          this.onUploadSettingsChanged(response.settings);
                          this.onCSVFileLoaded();                   
                      },
                      error:(error)=>{
                          this.globalService.toastrError(error)
                      },
                      complete:()=>{
                          this.onUploadCSVLoadingStateChange(false);
                      }
                  }
              );
      }  
    }

    onMergeOrganisationsChanged(mergeOrganisationsChanged: boolean){
        this.stepOrderService.mergeOrganisations = mergeOrganisationsChanged;
    }

    onContractCardsSearchChanged(search){
        this.contractCardItemsService.makeAContractControlSearch(search);
    }
}
